import { Head } from '@inertiajs/react'
import MainLayout from '../../components/layout/MainLayout'

/**
 * Page publique listant toutes les agences partenaires
 */
export default function AgencesIndex({ agencies }) {
  return (
    <MainLayout>
      <Head title="Nos agences partenaires - VitrineImmo" />

      <div className="bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Nos agences partenaires
              </h1>
              <p className="text-xl text-primary-100">
                Découvrez les agences immobilières qui utilisent VitrineImmo pour optimiser
                la gestion de leurs leads et améliorer leur relation client.
              </p>
            </div>
          </div>
        </div>

        {/* Agencies Grid */}
        <div className="container mx-auto px-4 py-12">
          {agencies.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-24 h-24 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Aucune agence pour le moment
              </h3>
              <p className="text-gray-500">
                Les agences partenaires apparaîtront bientôt ici.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agencies.map((agency) => (
                <AgencyCard key={agency.id} agency={agency} />
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}

/**
 * Carte d'affichage d'une agence
 */
function AgencyCard({ agency }) {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6">
      {/* Logo */}
      <div className="flex items-center gap-4 mb-4">
        {agency.logo_url ? (
          <img
            src={agency.logo_url}
            alt={`Logo ${agency.name}`}
            className="w-16 h-16 rounded-lg object-cover"
          />
        ) : (
          <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl font-bold text-primary-600">
              {agency.name.charAt(0)}
            </span>
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 line-clamp-1">
            {agency.name}
          </h3>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {agency.city}
          </p>
        </div>
      </div>

      {/* Address */}
      {agency.address && (
        <div className="mb-4 pb-4 border-b border-gray-100">
          <p className="text-sm text-gray-600">
            {agency.address}
            {agency.postal_code && `, ${agency.postal_code}`}
          </p>
        </div>
      )}

      {/* Contact Info */}
      <div className="space-y-2">
        <a
          href={`mailto:${agency.email}`}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-600 transition-colors"
        >
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <span className="truncate">{agency.email}</span>
        </a>

        <a
          href={`tel:${agency.phone}`}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-600 transition-colors"
        >
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <span>{agency.phone}</span>
        </a>

        {agency.website_url && (
          <a
            href={agency.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
              />
            </svg>
            <span>Visiter le site web</span>
          </a>
        )}
      </div>
    </div>
  )
}
